import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, setDoc, query, where, getDocs, deleteDoc, increment, writeBatch } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import TextAreaField from '../../components/ui/TextAreaField';
import EditIcon from '../../components/icons/EditIcon';
import DeleteIcon from '../../components/icons/TrashIcon';
import { Game } from '../../types/game';
import { User, Student, Teacher } from '../../utils/firebase';

// 리뷰 인터페이스 정의
interface Review {
  id: string;
  gameId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: Date;
  isGuest?: boolean;
}

const ANONYMOUS_REVIEWER_NAME = '익명';

const getGuestCommentStorageKey = (gameId: string) => `guestCommentId_${gameId}`;

const getOrCreateGuestId = (gameId: string) => {
  const key = getGuestCommentStorageKey(gameId);
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;

  const guestId = `guest_${
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`
  }`;
  sessionStorage.setItem(key, guestId);
  return guestId;
};

// 별점 컴포넌트
interface RatingProps {
  value: number;
  precision?: number;
  readOnly?: boolean;
  size?: 'small' | 'medium' | 'large';
  onChange?: (event: React.SyntheticEvent, newValue: number | null) => void;
}

const Rating: React.FC<RatingProps> = ({ 
  value, 
  precision = 1, 
  readOnly = false, 
  size = 'medium',
  onChange 
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const displayValue = hoverValue ?? value;
  const starSize = size === 'small' ? 16 : size === 'large' ? 32 : 24;

  const handleClick = (newValue: number) => {
    if (!readOnly && onChange) {
      onChange({} as React.SyntheticEvent, newValue);
    }
  };

  const handleMouseEnter = (newValue: number) => {
    if (!readOnly) {
      setHoverValue(newValue);
    }
  };

  const handleMouseLeave = () => {
    if (!readOnly) {
      setHoverValue(null);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          onClick={() => handleClick(star)}
          onMouseEnter={() => handleMouseEnter(star)}
          onMouseLeave={handleMouseLeave}
          style={{
            fontSize: `${starSize}px`,
            color: star <= displayValue ? '#FFB800' : '#E0E0E0',
            cursor: readOnly ? 'default' : 'pointer',
            transition: 'color 0.2s ease'
          }}
        >
          ★
        </span>
      ))}
      {precision === 0.1 && (
        <span style={{ 
          marginLeft: '8px', 
          fontSize: '14px', 
          color: '#161616',
          fontFamily: 'Pretendard'
        }}>
          {value.toFixed(1)}
        </span>
      )}
    </div>
  );
};

// 다이얼로그 컴포넌트
interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

const Dialog: React.FC<DialogProps> = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #F0F0F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#989898',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '24px' }}>
          {children}
        </div>
        {actions && (
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #F0F0F0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px'
          }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 게임 상세 페이지 컴포넌트
 * 게임 정보, 시뮬레이터, 리뷰 시스템을 포함
 */
const GameDetail: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();
  
  // 게임 관련 상태
  const [game, setGame] = useState<Game | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 사용자 리뷰 관련 상태
  const [userRating, setUserRating] = useState<number | null>(null);
  const [userReview, setUserReview] = useState('');
  const [hasRated, setHasRated] = useState(false);
  const [userReviewId, setUserReviewId] = useState<string | null>(null);
  
  // 리뷰 수정 관련 상태
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null);
  const [editReview, setEditReview] = useState('');
  const [editRating, setEditRating] = useState<number | null>(null);

  const simulatorLoadedRef = useRef(false);
  const playCountIncrementedRef = useRef(false);
  
  // 리뷰 삭제 관련 상태
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  
  // 게임 삭제 관련 상태
  const [openGameDeleteDialog, setOpenGameDeleteDialog] = useState(false);
  
  // 평가용 게임 관련 상태
  const [canReview, setCanReview] = useState(false);

  // 화면 표시용 평균 별점 — reviews에서 직접 계산해서 비로그인 게스트 작성 시에도 즉시 반영
  // (firestore rules상 비로그인은 games.rating 업데이트 불가)
  const averageRating = useMemo(() => {
    const rated = reviews.filter((r) => Number(r.rating) > 0);
    if (rated.length === 0) return game?.rating ?? 0;
    return rated.reduce((sum, r) => sum + Number(r.rating), 0) / rated.length;
  }, [reviews, game?.rating]);

  // 컴포넌트 마운트 시 게임 데이터 로드
  useEffect(() => {
    if (!gameId) {
      navigate('/makecodearcade');
      return;
    }

    simulatorLoadedRef.current = false;
    playCountIncrementedRef.current = false;
    fetchGame();
  }, [gameId]);

  // 게임 데이터가 로드되면 평가 권한 확인
  useEffect(() => {
    if (game) {
      console.log('게임 데이터 로드됨, checkCanReview 호출');
      checkCanReview(game);
    }
  }, [game, currentUser]);

  const getReviewDocId = useCallback(
    (authorId: string) => (gameId ? `${gameId}_${authorId}` : ''),
    [gameId]
  );

  const getCommentAuthorId = useCallback(() => {
    if (currentUser) return currentUser.uid;
    if (game?.allowGuestComments && gameId) return getOrCreateGuestId(gameId);
    return null;
  }, [currentUser, game?.allowGuestComments, gameId]);

  const canPostComment = useCallback(() => {
    if (!game) return false;
    if (!currentUser && !game.allowGuestComments) return false;

    if (game.isEvaluationGame && game.evaluationInfo) {
      const now = new Date();
      const inEvaluationPeriod =
        now >= game.evaluationInfo.startDate && now <= game.evaluationInfo.endDate;

      if (inEvaluationPeriod) {
        if (!currentUser) return false;
        if (['teacher', 'superAdmin', 'schoolAdmin'].includes(currentUser.role || '')) return true;
        return canReview;
      }
    }

    return true;
  }, [game, currentUser, canReview]);

  const syncUserReviewState = useCallback((reviewsList: Review[]) => {
    const authorId = currentUser
      ? currentUser.uid
      : game?.allowGuestComments && gameId
        ? getOrCreateGuestId(gameId)
        : null;

    if (!authorId) {
      setHasRated(false);
      setUserReviewId(null);
      setUserRating(null);
      setUserReview('');
      return;
    }

    const mine = reviewsList.find((review) => review.userId === authorId);
    if (mine) {
      setHasRated(true);
      setUserReviewId(mine.id);
      setUserRating(mine.rating);
      setUserReview(mine.comment || '');
      return;
    }

    setHasRated(false);
    setUserReviewId(null);
    setUserRating(null);
    setUserReview('');
  }, [currentUser, game?.allowGuestComments, gameId]);

  const incrementPlayCount = useCallback(async () => {
    if (!game?.id || playCountIncrementedRef.current) return;

    try {
      const playCountKey = `playCount_${game.id}`;
      if (sessionStorage.getItem(playCountKey)) {
        playCountIncrementedRef.current = true;
        return;
      }

      await updateDoc(doc(db, 'games', game.id), {
        playCount: increment(1),
      });

      sessionStorage.setItem(playCountKey, 'true');
      playCountIncrementedRef.current = true;
      setGame((prev) => (prev ? { ...prev, playCount: prev.playCount + 1 } : prev));
    } catch (error) {
      console.error('플레이 횟수 업데이트 중 오류:', error);
    }
  }, [game?.id]);

  useEffect(() => {
    syncUserReviewState(reviews);
  }, [reviews, syncUserReviewState]);

  /**
   * 게임 데이터를 Firestore에서 가져오는 함수
   */
  const fetchGame = async () => {
    if (!gameId) return;
    
    try {
      setLoading(true);
      setError('');

      // 게임 데이터 가져오기
      const gameDoc = await getDoc(doc(db, 'games', gameId));
      
      if (!gameDoc.exists()) {
        setError('게임을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      const gameData = gameDoc.data();
      const game: Game = {
        id: gameDoc.id,
        title: gameData.title,
        description: gameData.description ?? '',
        introduction: gameData.introduction,
        gameUrl: gameData.gameUrl,
        simulatorCode: gameData.simulatorCode,
        processUrl: gameData.processUrl,
        companyName: gameData.companyName,
        creatorName: gameData.creatorName,
        schoolName: gameData.schoolName,
        schoolId: gameData.schoolId,
        userId: gameData.userId,
        createdAt: gameData.createdAt?.toDate(),
        updatedAt: gameData.updatedAt?.toDate(),
        rating: gameData.rating,
        playCount: gameData.playCount,
        isEvaluationGame: gameData.isEvaluationGame,
        evaluationCode: gameData.evaluationCode,
        evaluationInfo: gameData.evaluationInfo ? {
          ...gameData.evaluationInfo,
          startDate: gameData.evaluationInfo.startDate.toDate(),
          endDate: gameData.evaluationInfo.endDate.toDate()
        } : undefined,
        iconUrl: gameData.iconUrl,
        commentsOnly: gameData.commentsOnly === true,
        allowGuestComments: gameData.allowGuestComments === true,
      };
      
      setGame(game);
      await fetchReviews();
    } catch (error) {
      console.error('게임 데이터 로딩 중 오류:', error);
      setError('게임을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 게임의 리뷰 목록을 가져오는 함수
   */
  const fetchReviews = async () => {
    if (!gameId) return;
    
    try {
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('gameId', '==', gameId)
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviewsData = reviewsSnapshot.docs
        .map((reviewDoc) => ({
          id: reviewDoc.id,
          ...reviewDoc.data(),
          createdAt: reviewDoc.data().createdAt?.toDate() ?? new Date(0),
        }))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) as Review[];
      setReviews(reviewsData);
    } catch (error) {
      console.error('리뷰 데이터 로딩 중 오류:', error);
    }
  };

  /**
   * 사용자가 평가할 수 있는지 확인하는 함수
   */
  const checkCanReview = (game: Game) => {
    console.log('=== checkCanReview 시작 ===');
    console.log('현재 사용자:', currentUser);
    console.log('게임 평가 정보:', game.evaluationInfo);

    if (!currentUser || !game.evaluationInfo) {
      console.log('사용자 정보 또는 평가 정보 없음');
      setCanReview(false);
      return;
    }

    // 교사인 경우 자신의 반을 평가할 수 있음
    if (currentUser.role === 'teacher') {
      const teacherUser = currentUser as Teacher;
      const teacherClass = teacherUser.homeroomClass;
      
      if (teacherClass) {
        const teacherClassStr = teacherClass.class.toString();
        const canReviewClass = Object.values(game.evaluationInfo.reviewerClasses).includes(teacherClassStr);
        
        if (canReviewClass) {
          console.log('교사가 자신의 반을 평가할 수 있음');
          setCanReview(true);
          return;
        }
      }
    }

    // 자신이 올린 게임인지 확인 (학생인 경우에만)
    if (currentUser.role === 'student' && currentUser.uid === game.userId) {
      console.log('학생이 자신이 올린 게임은 평가할 수 없음');
      setCanReview(false);
      return;
    }

    // 평가 기간 확인
    const now = new Date();
    const startDate = game.evaluationInfo.startDate;
    const endDate = game.evaluationInfo.endDate;

    // 평가 기간이 지났으면 모든 사용자가 리뷰 가능
    if (now > endDate) {
      console.log('평가 기간이 지남 - 모든 사용자 리뷰 가능');
      setCanReview(true);
      return;
    }

    // 평가 기간이 시작되지 않았으면 리뷰 불가
    if (now < startDate) {
      console.log('평가 기간이 시작되지 않음');
      setCanReview(false);
      return;
    }

    // 학교 ID 확인
    if (currentUser.schoolId !== game.evaluationInfo.schoolId) {
      console.log('다른 학교 게임');
      setCanReview(false);
      return;
    }

    // 학생 정보로 타입 단언
    const studentUser = currentUser as Student;

    // 학년 확인
    if (studentUser.grade !== game.evaluationInfo.grade) {
      console.log('다른 학년 게임');
      setCanReview(false);
      return;
    }

    // 반 정보 확인
    const userClass = studentUser.class.toString();
    const canReviewClass = Object.values(game.evaluationInfo.reviewerClasses).includes(userClass);
    console.log('평가 가능 여부:', canReviewClass);

    if (canReviewClass) {
      console.log('평가 가능한 반 확인됨 - 리뷰 가능');
      setCanReview(true);
    } else {
      console.log('평가 가능한 반이 아님');
      setCanReview(false);
    }
  };

  // 별점 변경 핸들러
  const handleRatingChange = (event: React.SyntheticEvent, newValue: number | null) => {
    setUserRating(newValue);
  };

  /**
   * 리뷰 제출 함수
   */
  const handleSubmitReview = async () => {
    const authorId = getCommentAuthorId();
    if (!authorId || !gameId) {
      setError(game?.commentsOnly ? '댓글을 작성하려면 로그인이 필요합니다.' : '리뷰를 작성하려면 로그인이 필요합니다.');
      return;
    }

    if (!canPostComment()) {
      setError('평가 기간이 아니거나 작성 권한이 없습니다.');
      return;
    }

    if (game?.commentsOnly && !userReview.trim()) {
      setError('댓글을 입력해주세요.');
      return;
    }

    if (!game?.commentsOnly && !userRating) {
      setError('별점을 선택해주세요.');
      return;
    }

    const isGuest = !currentUser;
    const rating = game?.commentsOnly ? 0 : (userRating || 0);

    try {
      if (hasRated && userReviewId && currentUser) {
        await updateDoc(doc(db, 'reviews', userReviewId), {
          rating,
          comment: userReview,
          userName: ANONYMOUS_REVIEWER_NAME,
          updatedAt: new Date(),
        });

        setSuccess(game?.commentsOnly ? '댓글이 수정되었습니다.' : '리뷰가 수정되었습니다.');
      } else if (hasRated && isGuest) {
        setError('비로그인으로 등록한 리뷰는 수정할 수 없습니다.');
        return;
      } else {
        const reviewDocId = getReviewDocId(authorId);
        await setDoc(doc(db, 'reviews', reviewDocId), {
          gameId,
          userId: authorId,
          userName: ANONYMOUS_REVIEWER_NAME,
          rating,
          comment: userReview.trim(),
          createdAt: new Date(),
          ...(isGuest ? { isGuest: true } : {}),
        });

        setUserReviewId(reviewDocId);
        setSuccess(game?.commentsOnly ? '댓글이 등록되었습니다.' : '리뷰가 등록되었습니다.');
        setHasRated(true);
      }

      // games.rating 업데이트는 로그인 사용자만 시도(비로그인은 firestore rules로 거부됨).
      // 화면 평균 별점은 reviews에서 직접 계산하므로 즉시 반영됨.
      if (!game?.commentsOnly && currentUser) {
        await updateGameRating();
      }

      fetchReviews();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      setError(game?.commentsOnly ? '댓글 등록 중 오류가 발생했습니다.' : '리뷰 등록 중 오류가 발생했습니다.');
      setTimeout(() => setError(''), 3000);
    }
  };

  /**
   * 게임의 평균 별점을 업데이트하는 함수
   */
  const updateGameRating = async () => {
    if (!gameId) return;
    
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(reviewsRef, where('gameId', '==', gameId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        await updateDoc(doc(db, 'games', gameId), {
          rating: 0
        });
        
        if (game) {
          setGame({
            ...game,
            rating: 0
          });
        }
        return;
      }
      
      const ratedReviews = querySnapshot.docs.filter(
        (reviewDoc) => Number(reviewDoc.data().rating) > 0
      );

      if (ratedReviews.length === 0) {
        await updateDoc(doc(db, 'games', gameId), { rating: 0 });
        if (game) setGame({ ...game, rating: 0 });
        return;
      }

      let totalRating = 0;
      ratedReviews.forEach((reviewDoc) => {
        totalRating += Number(reviewDoc.data().rating) || 0;
      });

      const averageRating = totalRating / ratedReviews.length;
      
      await updateDoc(doc(db, 'games', gameId), {
        rating: averageRating
      });
      
      if (game) {
        setGame({
          ...game,
          rating: averageRating
        });
      }
    } catch (error) {
      console.error('Error updating game rating:', error);
    }
  };

  // 리뷰 수정 다이얼로그 열기
  const handleEditReview = (review: Review) => {
    setEditingReviewId(review.id);
    setEditReview(review.comment);
    setEditRating(review.rating);
    setOpenEditDialog(true);
  };

  /**
   * 리뷰 수정 저장 함수
   */
  const handleSaveEdit = async () => {
    if (!editingReviewId || !editRating) {
      setError('별점을 선택해주세요.');
      return;
    }
    
    try {
      await updateDoc(doc(db, 'reviews', editingReviewId), {
        rating: editRating,
        comment: editReview,
        userName: ANONYMOUS_REVIEWER_NAME,
        updatedAt: new Date()
      });
      
      setOpenEditDialog(false);
      setSuccess('리뷰가 수정되었습니다.');
      
      await updateGameRating();
      fetchReviews();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating review:', error);
      setError('리뷰 수정 중 오류가 발생했습니다.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // 리뷰 삭제 다이얼로그 열기
  const handleDeleteReview = (reviewId: string) => {
    setReviewToDelete(reviewId);
    setOpenDeleteDialog(true);
  };

  /**
   * 리뷰 삭제 확인 함수
   */
  const handleConfirmDelete = async () => {
    if (!reviewToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'reviews', reviewToDelete));
      setOpenDeleteDialog(false);
      setSuccess('리뷰가 삭제되었습니다.');

      if (reviewToDelete === userReviewId) {
        setHasRated(false);
        setUserReviewId(null);
        setUserRating(null);
        setUserReview('');
      }
      
      await updateGameRating();
      fetchReviews();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('리뷰 삭제 중 오류가 발생했습니다.');
      setTimeout(() => setError(''), 3000);
    }
  };

  /**
   * 게임 삭제 확인 함수
   */
  const handleConfirmGameDelete = async () => {
    if (!gameId || !game) return;
    
    try {
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('gameId', '==', gameId)
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      const batch = writeBatch(db);
      
      reviewsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      batch.delete(doc(db, 'games', gameId));
      
      await batch.commit();
      
      setOpenGameDeleteDialog(false);
      setSuccess('게임이 삭제되었습니다.');
      
      setTimeout(() => {
        navigate('/makecodearcade');
      }, 1500);
    } catch (error) {
      console.error('Error deleting game:', error);
      setError('게임 삭제 중 오류가 발생했습니다.');
      setTimeout(() => setError(''), 3000);
    }
  };

  /**
   * 게임 플레이 함수
   */
  const handlePlayGame = async () => {
    if (!game) return;
    try {
      window.open(game.gameUrl, '_blank');
    } catch (error) {
      console.error('Error opening game:', error);
    }
  };

  /**
   * MakeCode 시뮬레이터 렌더링 함수
   */
  const renderSimulator = () => {
    if (!game?.simulatorCode) return null;

    const match = game.simulatorCode.match(/id=([^"&]+)/);
    const simulatorGameId = match ? match[1] : null;

    if (!simulatorGameId) return null;

    const handleSimulatorLoad = async () => {
      console.log('시뮬레이터 iframe 로드됨');
      simulatorLoadedRef.current = true;
      await incrementPlayCount();
    };

    return (
      <div style={{ width: '100%' }}>
        {/* MakeCode 공식 embed는 가로 대비 약 122% 세로. 옆 정보 칸 높이에 맞추면 아래 조작 버튼이 잘림. */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 0,
            paddingBottom: '122%',
            overflow: 'hidden',
            borderRadius: '12px',
            backgroundColor: 'var(--Color-fill-normal, #293044)',
          }}
        >
          <iframe
            title="MakeCode Arcade 시뮬레이터"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            src={`https://arcade.makecode.com/---run?id=${simulatorGameId}`}
            allowFullScreen
            sandbox="allow-popups allow-forms allow-scripts allow-same-origin"
            frameBorder="0"
            onLoad={handleSimulatorLoad}
          />
        </div>
      </div>
    );
  };

  // 관리자 권한 확인
  const isAdmin = currentUser && ['teacher', 'superAdmin', 'schoolAdmin'].includes(currentUser.role || '');
  const isSuperAdmin = currentUser && currentUser.role === 'superAdmin';
  const isEvaluationPeriod = game?.isEvaluationGame && game.evaluationInfo && new Date() <= game.evaluationInfo.endDate;

  // 로딩 상태 렌더링
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #F0F0F0',
          borderTop: '4px solid #FF6F0F',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // 게임이 없는 경우 렌더링
  if (!game) {
    return (
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <div style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#FF3838',
          marginBottom: '16px',
          fontFamily: 'Pretendard'
        }}>
          게임을 찾을 수 없습니다.
        </div>
        <Button onClick={() => navigate('/makecodearcade')}>
          돌아가기
        </Button>
      </div>
    );
  }

  return (
    <div
      style={getMakecodeArcadePageRootStyle(isMobile, {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      })}
    >
        {/* 에러/성공 메시지 */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#FFF4F4',
            border: '1px solid #FF3838',
            borderRadius: '8px',
            color: '#FF3838',
            fontSize: '14px',
            fontFamily: 'Pretendard'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#F0FDF4',
            border: '1px solid #22C55E',
            borderRadius: '8px',
            color: '#22C55E',
            fontSize: '14px',
            fontFamily: 'Pretendard'
          }}>
            {success}
          </div>
        )}

        {/* 게임 정보 카드 */}
        <Card width="100%" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
          <div style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '24px',
            marginBottom: '24px',
            alignItems: 'stretch',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box'
          }}>
            {/* 시뮬레이터: 너비 고정 (긴 설명으로 줄어들지 않음) */}
            <div style={{
              flex: isMobile ? '1 1 100%' : '0 0 480px',
              flexShrink: 0,
              width: isMobile ? '100%' : '480px',
              maxWidth: isMobile ? '100%' : '480px',
              alignSelf: 'flex-start',
            }}>
              {renderSimulator()}
            </div>

            {/* 게임 정보 */}
            <div style={{
              flex: '1 1 0',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--gap-level-4)',
              overflow: 'hidden',
            }}>
              {/* 메타: 로고 ~ 등록일 (한 영역) */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: 'var(--Color-background-main)',
                  borderRadius: '12px',
                  border: '1px solid var(--Color-line-normal)',
                  boxSizing: 'border-box',
                }}
              >
                {game.iconUrl && (
                  <img
                    src={game.iconUrl}
                    alt={game.title}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    gap: '8px',
                  }}>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '600',
                      color: '#161616',
                      fontFamily: 'Pretendard',
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {game.title}
                      {game.isEvaluationGame && (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#FF6F0F',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          평가용 게임
                        </span>
                      )}
                    </div>
                    {isSuperAdmin && (
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => setOpenGameDeleteDialog(true)}
                        rightIcon={<DeleteIcon />}
                      >
                        게임 삭제
                      </Button>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: '#E3F2FD',
                      color: '#1976D2',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {game.schoolName}
                    </span>
                    {game.companyName && (
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: '#F3E5F5',
                        color: '#7B1FA2',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {game.companyName}
                      </span>
                    )}
                  </div>

                  <div style={{
                    fontSize: '14px',
                    color: '#989898',
                    marginBottom: '4px',
                    fontFamily: 'Pretendard'
                  }}>
                    제작자: {game.creatorName}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#989898',
                    marginBottom: 0,
                    fontFamily: 'Pretendard'
                  }}>
                    등록일: {game.createdAt.toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* 게임 설명: 메타 영역 아래 전체 너비 */}
              {(game.description?.trim() || game.introduction?.trim()) && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: 'var(--Color-background-main)',
                    borderRadius: '12px',
                    border: '1px solid var(--Color-line-normal)',
                    boxSizing: 'border-box',
                    width: '100%',
                  }}
                >
                  {game.description?.trim() && (
                    <>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'var(--Color-text-normal)',
                          marginBottom: '8px',
                          fontFamily: 'var(--font-family-primary)',
                        }}
                      >
                        게임 설명
                      </div>
                      <div
                        style={{
                          fontSize: '15px',
                          lineHeight: '22px',
                          color: 'var(--Color-text-strong)',
                          fontFamily: 'var(--font-family-primary)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {game.description}
                      </div>
                    </>
                  )}
                  {game.introduction?.trim() && (
                    <div
                      style={{
                        marginTop: game.description?.trim() ? '12px' : 0,
                        fontSize: '14px',
                        lineHeight: '21px',
                        color: 'var(--Color-text-normal)',
                        fontFamily: 'var(--font-family-primary)',
                        fontStyle: 'italic',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {`"${game.introduction}"`}
                    </div>
                  )}
                </div>
              )}

              {/* 평점·플레이 (한 행) */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '24px',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>⭐</span>
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#989898',
                      marginBottom: '4px'
                    }}>
                      평균 별점
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Rating value={averageRating} readOnly precision={0.1} size="small" />
                      <span style={{
                        fontSize: '14px',
                        color: '#FF6F0F',
                        fontWeight: '600'
                      }}>
                        {averageRating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>▶</span>
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#989898',
                      marginBottom: '4px'
                    }}>
                      플레이 횟수
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#FF6F0F',
                      fontWeight: '600'
                    }}>
                      {game.playCount.toLocaleString()}회
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                height: '1px',
                backgroundColor: '#F0F0F0',
                marginBottom: '24px'
              }} />

              {/* 게임 액션 버튼들 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <Button
                  variant="primary"
                  onClick={handlePlayGame}
                  leftIcon={<span>▶</span>}
                >
                  게임 플레이 바로가기(메이크코드 아케이드)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(game.processUrl, '_blank')}
                  leftIcon={<span>💻</span>}
                >
                  제작 과정 보기
                </Button>
              </div>

              {/* 평가 기간 정보 */}
              {isEvaluationPeriod && game.evaluationInfo && (
                <Card
                  background="#F8F9FA"
                  padding="16px"
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>ℹ️</span>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#161616'
                    }}>
                      평가중입니다
                    </div>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#161616',
                    marginBottom: '12px'
                  }}>
                    평가 기간: {game.evaluationInfo.startDate.toLocaleDateString()} ~ {game.evaluationInfo.endDate.toLocaleDateString()}
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#E3F2FD',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#1976D2'
                  }}>
                    평가중에는 특정 반을 제외하고는 리뷰와 댓글을 남길 수 없습니다.
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Card>

        {/* 리뷰/댓글 섹션 */}
        <div style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#161616',
          marginBottom: '16px',
          fontFamily: 'Pretendard'
        }}>
          {game.commentsOnly ? '댓글' : '리뷰'}
        </div>

        {/* 작성 폼 */}
        {currentUser || game.allowGuestComments ? (
          <Card>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#161616',
              marginBottom: '16px',
              fontFamily: 'Pretendard'
            }}>
              {hasRated
                ? (game.commentsOnly ? '댓글 수정' : '리뷰 수정')
                : (game.commentsOnly ? '댓글 작성' : '리뷰 작성')}
            </div>

            {isEvaluationPeriod && !canReview && !isAdmin && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#FFF4E6',
                border: '1px solid #FFB800',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#161616'
              }}>
                🔒 이 게임은 평가용 게임으로, 지정된 학급의 학생들만 {game.commentsOnly ? '댓글' : '리뷰'}을 남길 수 있습니다.
              </div>
            )}

            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F4F6FA',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#161616'
            }}>
              {game.commentsOnly
                ? '댓글은 익명으로 등록되며, 다른 사용자에게 이름이 표시되지 않습니다.'
                : '별점과 리뷰는 익명으로 등록되며, 다른 사용자에게 이름이 표시되지 않습니다.'}
              {!currentUser && game.allowGuestComments && (
                <span>
                  {game.commentsOnly
                    ? ' 로그인 없이도 댓글을 남길 수 있습니다.'
                    : ' 로그인 없이도 별점과 리뷰를 남길 수 있습니다.'}
                  {' '}(이 브라우저에서는 한 번만 등록할 수 있습니다.)
                </span>
              )}
            </div>

            {!game.commentsOnly && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px',
                  fontFamily: 'Pretendard'
                }}>
                  별점
                </div>
                <Rating
                  value={userRating || 0}
                  onChange={handleRatingChange}
                  precision={0.5}
                  readOnly={!canPostComment()}
                />
              </div>
            )}

            <TextAreaField
              value={userReview}
              onChange={setUserReview}
              placeholder={game.commentsOnly ? '댓글을 작성해주세요' : '리뷰를 작성해주세요 (선택)'}
              disabled={!canPostComment() || (hasRated && !currentUser)}
              width="100%"
              rows={4}
            />

            <div style={{ marginTop: '16px' }}>
              <Button
                variant="primary"
                onClick={handleSubmitReview}
                disabled={!canPostComment() || (hasRated && !currentUser)}
              >
                {hasRated
                  ? (currentUser ? (game.commentsOnly ? '댓글 수정' : '리뷰 수정') : '등록 완료')
                  : (game.commentsOnly ? '댓글 등록' : '별점·리뷰 등록')}
              </Button>
            </div>
          </Card>
        ) : (
          <Card background="#E3F2FD">
            <div style={{
              fontSize: '14px',
              color: '#1976D2',
              fontFamily: 'Pretendard'
            }}>
              {game.commentsOnly ? '댓글' : '리뷰'}을 작성하려면 로그인이 필요합니다.
            </div>
          </Card>
        )}

        {/* 리뷰 목록 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {reviews.map((review) => (
            <Card key={review.id}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#161616',
                    marginBottom: '4px',
                    fontFamily: 'Pretendard'
                  }}>
                    {ANONYMOUS_REVIEWER_NAME}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#989898',
                    fontFamily: 'Pretendard'
                  }}>
                    {review.createdAt.toLocaleDateString()} {review.createdAt.toLocaleTimeString()}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  {review.rating > 0 && (
                    <Rating value={review.rating} readOnly precision={0.5} size="small" />
                  )}
                  {currentUser && !review.isGuest && (currentUser.uid === review.userId || isAdmin) && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditReview(review)}
                        disabled={currentUser?.uid !== review.userId && !isAdmin}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: currentUser?.uid === review.userId || isAdmin ? 'pointer' : 'not-allowed',
                          opacity: currentUser?.uid === review.userId || isAdmin ? 1 : 0.5,
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FF3838'
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{
                fontSize: '14px',
                color: '#161616',
                lineHeight: '1.5',
                fontFamily: 'Pretendard'
              }}>
                {review.comment}
              </div>
            </Card>
          ))}

          {reviews.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#989898',
              fontSize: '14px',
              fontFamily: 'Pretendard'
            }}>
              아직 리뷰가 없습니다. 첫 번째 리뷰를 작성해보세요!
            </div>
          )}
        </div>

        {/* 리뷰 수정 다이얼로그 */}
        <Dialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          title="리뷰 수정"
          actions={
            <>
              <Button variant="cancel" onClick={() => setOpenEditDialog(false)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleSaveEdit}>
                저장
              </Button>
            </>
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#161616',
              marginBottom: '8px',
              fontFamily: 'Pretendard'
            }}>
              별점
            </div>
            <Rating
              value={editRating || 0}
              onChange={(e, newValue) => setEditRating(newValue)}
              precision={0.5}
            />
          </div>
          <TextAreaField
            value={editReview}
            onChange={setEditReview}
            placeholder="리뷰를 작성해주세요"
            width="100%"
            rows={4}
          />
        </Dialog>

        {/* 리뷰 삭제 확인 다이얼로그 */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          title="리뷰 삭제"
          actions={
            <>
              <Button variant="cancel" onClick={() => setOpenDeleteDialog(false)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleConfirmDelete}>
                삭제
              </Button>
            </>
          }
        >
          <div style={{
            fontSize: '14px',
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            정말로 이 리뷰를 삭제하시겠습니까?
          </div>
        </Dialog>

        {/* 게임 삭제 확인 다이얼로그 */}
        <Dialog
          open={openGameDeleteDialog}
          onClose={() => setOpenGameDeleteDialog(false)}
          title="게임 삭제"
          actions={
            <>
              <Button variant="cancel" onClick={() => setOpenGameDeleteDialog(false)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleConfirmGameDelete}>
                삭제
              </Button>
            </>
          }
        >
          <div style={{
            fontSize: '14px',
            color: '#161616',
            marginBottom: '16px',
            fontFamily: 'Pretendard'
          }}>
            정말로 이 게임을 삭제하시겠습니까?
          </div>
          <div style={{
            padding: '12px',
            backgroundColor: '#FFF4E6',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#FF6F0F'
          }}>
            이 작업은 되돌릴 수 없습니다. 게임과 관련된 모든 리뷰도 함께 삭제됩니다.
          </div>
        </Dialog>
      </div>
  );
};

export default GameDetail;
